import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

interface Task {
  task: string;
  start: Date;
  end: Date;
  dependencies: string; // List of task names this task depends on
}

interface GanttChartProps {
  data: Task[];
}

const GanttChart: React.FC<GanttChartProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    // Define chart dimensions
    const margin = { top: 20, right: 30, bottom: 30, left: 100 };
    const svgContainer: any = d3.select(chartRef.current).node();
    const width = svgContainer.getBoundingClientRect()?.width || 800;
    const height = 400 - margin.top - margin.bottom;

    // Create scales
    const x = d3.scaleTime().domain([
      d3.min(data, function (d) {
        return d.start;
      })|| new Date(2024, 0, 1),
      d3.max(data, function (d) {
        return d.end;
      })||new Date(2024, 0, 1)
    ]).range([0, width - margin.left - margin.right]);
    const y = d3.scaleBand().domain(data.map(d => d.task)).range([0, height]).padding(0.1);

    // Add axes
    svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .call(d3.axisTop(x).ticks(d3.timeWeek.every(1)));

    svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .call(d3.axisLeft(y));

    // Add bars for main tasks
    svg.selectAll('.bar')
      .data(data)
      .enter().append('rect')
      .attr('class', 'bar')
      .attr('x', d => x(d.start) + margin.left)
      .attr('y', d => y(d.task)! + margin.top)
      .attr('width', d => x(d.end) - x(d.start))
      
      .attr('fill', (d) =>  d.dependencies =="" ? "pink":"blue")
      .attr('height', y.bandwidth())
      .append('title')
      .text(d => `${d.task}\n${d.start.toDateString()} - ${d.end.toDateString()}`);

    // Add dependencies as paths with arrows from top of main task to bottom of inner task
    const markerWidth = 8;
    const markerHeight = 8;
    svg.append('defs')
      .append('marker')
      .attr('id', 'arrow')
      .attr('markerWidth', markerWidth)
      .attr('markerHeight', markerHeight)
      .attr('refX', markerWidth / 2)
      .attr('refY', markerHeight / 2)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,0 L0,8 L8,4 z')
      .attr('fill', 'red'); // Rotate the arrow to point downwards
    data.forEach(d => {
      if (d.dependencies) {
        
          const dependency = data.find(task => task.task === d.dependencies);
          if (dependency) {
            svg.append('path')
              .attr('d', () => {
                const startX = x(d.start) + margin.left + (x(d.end) - x(d.start)) / 2;
                const startY = y(d.task)! + margin.top;
                const endX = x(dependency.start) + margin.left + (x(dependency.end) - x(dependency.start)) / 2;
                const endY = y(dependency.task)! + margin.top + y.bandwidth();
                return `M ${endX} ${endY} L ${startX} ${startY}`;
              })
              .attr('fill', 'none')
              .attr('stroke', 'red') // Change path color to red
              .attr('marker-end', 'url(#arrow)');
          }
        
      }
    });

   

  }, [data]);

  return (
    <div className="" ref={chartRef}>
      <svg ref={svgRef} width="100%" height="400">
        <g transform="translate(40,20)"></g>
      </svg>
    </div>
  );
};

export default GanttChart;
